/**
 * BgmLibraryDialog — per-user BGM track library (个性化曲目库) browser.
 * [REFACTOR-2026-06-07]
 *
 *   - the user's tracks (seed baseline + their uploads), metadata from the DB,
 *   - filter chips by tag (union of all tags in the library),
 *   - inline audition (试听) — play/pause one track at a time via the authed
 *     axios client (the bgm/file endpoint is JWT-protected),
 *   - per-track editing: rename, add/remove tags, delete — full personalisation,
 *   - select a track for the render, or upload a new one (with tags).
 */
import { useEffect, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Check, Loader2, Music, Pause, Pencil, Play, Plus, Trash2, Upload, X } from 'lucide-react'

import { api } from '@/lib/api'
import { dramaApi } from '@/lib/api/drama'
import type { BgmTrack, DramaLang } from '@/types/drama'

import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { toast } from '@/components/ui/toaster'
import { dramaT } from './i18n'

// The 6 mood presets seed-tracks ship with; offered as tag suggestions. Tags
// are free-form strings — these are just convenient starting points.
const MOOD_PRESETS = ['tense', 'romantic', 'warm', 'low_turning_point', 'conflict', 'sneaky']

interface Props {
  open: boolean
  onOpenChange: (o: boolean) => void
  projectId: string
  lang: DramaLang
  selectedTrackId?: string | null
  onSelect: (track: BgmTrack) => void
}

export function BgmLibraryDialog({ open, onOpenChange, projectId, lang, selectedTrackId, onSelect }: Props) {
  const t = dramaT(lang)
  const queryClient = useQueryClient()
  const [tagFilter, setTagFilter] = useState<string>('all')
  const [uploadTags, setUploadTags] = useState('')
  const [editingName, setEditingName] = useState<string | null>(null)
  const [addingTagFor, setAddingTagFor] = useState<string | null>(null)
  const fileRef = useRef<HTMLInputElement | null>(null)

  // Audition: one track at a time through a single shared <audio>.
  const audioRef = useRef<HTMLAudioElement | null>(null)
  const blobCache = useRef<Map<string, string>>(new Map())
  const [playingId, setPlayingId] = useState<string | null>(null)
  const [loadingId, setLoadingId] = useState<string | null>(null)

  const { data: tracks = [], isLoading } = useQuery({
    queryKey: ['drama-bgm-library', projectId],
    queryFn: () => dramaApi.bgmLibrary(projectId),
    enabled: open,
  })

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['drama-bgm-library', projectId] })

  useEffect(() => {
    if (open) return
    audioRef.current?.pause()
    setPlayingId(null)
    blobCache.current.forEach((u) => URL.revokeObjectURL(u))
    blobCache.current.clear()
  }, [open])

  const stop = () => {
    audioRef.current?.pause()
    setPlayingId(null)
  }

  const audition = async (track: BgmTrack) => {
    if (playingId === track.id) {
      stop()
      return
    }
    try {
      let blobUrl = blobCache.current.get(track.id)
      if (!blobUrl) {
        setLoadingId(track.id)
        const res = await api.client.get(dramaApi.bgmFileUrl(projectId, track.id), { responseType: 'blob' })
        blobUrl = URL.createObjectURL(res.data as Blob)
        blobCache.current.set(track.id, blobUrl)
      }
      const el = audioRef.current
      if (!el) return
      el.src = blobUrl
      el.currentTime = 0
      await el.play()
      setPlayingId(track.id)
    } catch {
      toast({ title: t('bgm.auditionFailed'), variant: 'destructive' })
    } finally {
      setLoadingId(null)
    }
  }

  const patchMutation = useMutation({
    mutationFn: (v: { id: string; patch: { name?: string; tags?: string[] } }) =>
      dramaApi.patchBgmTrack(projectId, v.id, v.patch),
    onSuccess: () => invalidate(),
    onError: (e: any) =>
      toast({ title: t('bgm.editFailed'), description: e?.response?.data?.detail || e.message, variant: 'destructive' }),
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => dramaApi.deleteBgmTrack(projectId, id),
    onSuccess: () => {
      invalidate()
      toast({ title: t('bgm.deleted') })
    },
    onError: (e: any) =>
      toast({ title: t('bgm.editFailed'), description: e?.response?.data?.detail || e.message, variant: 'destructive' }),
  })

  const uploadMutation = useMutation({
    mutationFn: (file: File) =>
      dramaApi.uploadBgm(projectId, file, {
        tags: uploadTags.split(',').map((s) => s.trim()).filter(Boolean),
      }),
    onSuccess: (track) => {
      invalidate()
      setUploadTags('')
      toast({ title: t('bgm.uploadDone') })
      onSelect(track)
    },
    onError: (e: any) =>
      toast({ title: t('toast.bgmUploadFailed'), description: e?.response?.data?.detail || e.message, variant: 'destructive' }),
  })

  const addTag = (track: BgmTrack, raw: string) => {
    const tag = raw.trim()
    setAddingTagFor(null)
    if (!tag || track.tags.includes(tag)) return
    patchMutation.mutate({ id: track.id, patch: { tags: [...track.tags, tag] } })
  }
  const removeTag = (track: BgmTrack, tag: string) =>
    patchMutation.mutate({ id: track.id, patch: { tags: track.tags.filter((x) => x !== tag) } })

  const tagLabel = (tag: string) => (MOOD_PRESETS.includes(tag) ? t(`bgm.${tag}` as any) : tag)

  const allTags = Array.from(new Set(tracks.flatMap((tr) => tr.tags))).sort()
  const visible = tagFilter === 'all' ? tracks : tracks.filter((tr) => tr.tags.includes(tagFilter))

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Music className="w-4 h-4 text-spectrum-magenta" />
            {t('bgm.libraryTitle')}
          </DialogTitle>
          <DialogDescription>{t('bgm.libraryDesc')}</DialogDescription>
        </DialogHeader>

        <audio ref={audioRef} onEnded={() => setPlayingId(null)} className="hidden" />

        {/* tag filter chips */}
        <div className="flex flex-wrap gap-1.5">
          <button
            onClick={() => setTagFilter('all')}
            className={`px-2.5 py-1 rounded-full text-xs border transition-colors ${
              tagFilter === 'all' ? 'bg-foreground text-background' : 'bg-background hover:bg-muted'
            }`}
          >
            {t('bgm.filterAll')} ({tracks.length})
          </button>
          {allTags.map((tag) => {
            const n = tracks.filter((tr) => tr.tags.includes(tag)).length
            return (
              <button
                key={tag}
                onClick={() => setTagFilter(tag)}
                className={`px-2.5 py-1 rounded-full text-xs border transition-colors ${
                  tagFilter === tag ? 'bg-foreground text-background' : 'bg-background hover:bg-muted'
                }`}
              >
                {tagLabel(tag)} ({n})
              </button>
            )
          })}
        </div>

        {/* track list */}
        <div className="max-h-[44vh] overflow-y-auto -mx-1 px-1 space-y-1.5">
          {isLoading ? (
            <div className="py-10 text-center">
              <Loader2 className="w-5 h-5 animate-spin mx-auto text-muted-foreground" />
            </div>
          ) : visible.length === 0 ? (
            <div className="py-10 text-center text-sm text-muted-foreground">{t('bgm.empty')}</div>
          ) : (
            visible.map((track) => {
              const isSel = track.id === selectedTrackId
              const isPlaying = playingId === track.id
              return (
                <div
                  key={track.id}
                  className={`flex items-start gap-3 px-3 py-2 rounded-lg border ${
                    isSel ? 'border-spectrum-magenta bg-spectrum-magenta/5' : 'border-border'
                  }`}
                >
                  <button
                    onClick={() => audition(track)}
                    className="shrink-0 mt-0.5 w-8 h-8 rounded-full bg-muted hover:bg-muted/70 flex items-center justify-center"
                    aria-label={t('bgm.audition')}
                  >
                    {loadingId === track.id ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : isPlaying ? (
                      <Pause className="w-4 h-4" />
                    ) : (
                      <Play className="w-4 h-4" />
                    )}
                  </button>

                  <div className="min-w-0 flex-1">
                    {/* name (inline editable) */}
                    {editingName === track.id ? (
                      <Input
                        autoFocus
                        defaultValue={track.name}
                        className="h-7 text-sm"
                        onBlur={(e) => {
                          setEditingName(null)
                          const v = e.target.value.trim()
                          if (v && v !== track.name) patchMutation.mutate({ id: track.id, patch: { name: v } })
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') (e.target as HTMLInputElement).blur()
                          if (e.key === 'Escape') setEditingName(null)
                        }}
                      />
                    ) : (
                      <button
                        className="text-sm font-medium truncate flex items-center gap-1 group"
                        onClick={() => setEditingName(track.id)}
                      >
                        {track.name}
                        <Pencil className="w-3 h-3 opacity-0 group-hover:opacity-60" />
                      </button>
                    )}

                    {/* tags (editable chips) */}
                    <div className="flex flex-wrap items-center gap-1 mt-1">
                      {track.tags.map((tag) => (
                        <Badge key={tag} variant="secondary" className="text-[10px] pr-1 gap-1">
                          {tagLabel(tag)}
                          <button onClick={() => removeTag(track, tag)} className="hover:text-destructive">
                            <X className="w-2.5 h-2.5" />
                          </button>
                        </Badge>
                      ))}
                      {addingTagFor === track.id ? (
                        <input
                          autoFocus
                          list="bgm-tag-suggestions"
                          className="h-5 w-24 text-[11px] border rounded px-1 bg-background"
                          placeholder={t('bgm.newTag')}
                          onBlur={(e) => addTag(track, e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') (e.target as HTMLInputElement).blur()
                            if (e.key === 'Escape') setAddingTagFor(null)
                          }}
                        />
                      ) : (
                        <button
                          onClick={() => setAddingTagFor(track.id)}
                          className="text-[10px] text-muted-foreground hover:text-foreground inline-flex items-center gap-0.5 border border-dashed rounded-full px-1.5 py-0.5"
                        >
                          <Plus className="w-2.5 h-2.5" /> {t('bgm.addTag')}
                        </button>
                      )}
                      <span className="text-[10px] text-muted-foreground ml-1">
                        {track.source === 'custom' ? t('bgm.uploaded') : t('bgm.builtin')}
                      </span>
                    </div>
                  </div>

                  <div className="flex items-center gap-1 shrink-0">
                    <Button
                      size="sm"
                      variant={isSel ? 'default' : 'outline'}
                      onClick={() => {
                        stop()
                        onSelect(track)
                      }}
                    >
                      {isSel ? <Check className="w-3.5 h-3.5 mr-1" /> : null}
                      {isSel ? t('bgm.selected') : t('bgm.select')}
                    </Button>
                    <Button
                      size="icon"
                      variant="ghost"
                      className="h-8 w-8 text-muted-foreground hover:text-destructive"
                      title={t('bgm.delete')}
                      onClick={() => deleteMutation.mutate(track.id)}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
              )
            })
          )}
        </div>

        {/* tag suggestions shared by all the add-tag inputs */}
        <datalist id="bgm-tag-suggestions">
          {Array.from(new Set([...MOOD_PRESETS, ...allTags])).map((tag) => (
            <option key={tag} value={tag} />
          ))}
        </datalist>

        {/* upload */}
        <div className="border-t pt-3 flex items-end gap-2 flex-wrap">
          <input
            ref={fileRef}
            type="file"
            accept="audio/*"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0]
              e.target.value = ''
              if (f) uploadMutation.mutate(f)
            }}
          />
          <div className="flex-1 min-w-[160px]">
            <Label className="text-xs">{t('bgm.uploadTags')}</Label>
            <Input
              value={uploadTags}
              onChange={(e) => setUploadTags(e.target.value)}
              placeholder={t('bgm.uploadTagsPlaceholder')}
              className="h-8 mt-1 text-sm"
            />
          </div>
          <Button
            size="sm"
            variant="outline"
            disabled={uploadMutation.isPending}
            onClick={() => fileRef.current?.click()}
          >
            {uploadMutation.isPending ? (
              <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />
            ) : (
              <Upload className="w-3.5 h-3.5 mr-1" />
            )}
            {uploadMutation.isPending ? t('render.bgmUploading') : t('bgm.uploadTrack')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
